import { useState, useMemo } from "react";
import { useAddNewCampaignMutation } from "./campaignsApiSlice";
import { useNavigate } from "react-router-dom";

export default function NewCampaignForm() {
  const navigate = useNavigate();
  const [addNewCampaign, { isLoading, isError, error }] = useAddNewCampaignMutation();

  // Form state
  const [title, setTitle] = useState("");
  const [shortTitle, setShortTitle] = useState("");
  const [description, setDescription] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [status, setStatus] = useState("open");


  // --- Validation (same rules as Edit) ---
  const validTitle = title.trim().length >= 3 && title.trim().length <= 80;
  const validShort = shortTitle.trim().length >= 3 && shortTitle.trim().length <= 60;
  const validDesc = description.trim().length > 0;
  const validDates = useMemo(() => {
    if (!startDate || !endDate) return false;
    const s = new Date(startDate);
    const e = new Date(endDate);
    return !Number.isNaN(s) && !Number.isNaN(e) && s <= e;
  }, [startDate, endDate]);

  const canSave =
    validTitle && validShort && validDesc && validDates && !isLoading;

  const onSaveCampaignClicked = async (e) => {
    e.preventDefault();
    if (!canSave) return;
    try {
      await addNewCampaign({
        title,
        shortTitle,
        description,
        startDate,
        endDate,
        status
      }).unwrap();
      navigate("/dash/campaigns");
    } catch {
      /* handled by error banner */
    }
  };

  return (
    <form
      onSubmit={(e) => e.preventDefault()}
      className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm space-y-6"
    >
      {/* Error banner */}
      {isError && (
        <div className="rounded-xl border border-rose-200 bg-rose-50 p-3 text-rose-700">
          {error?.data?.message || "Something went wrong."}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Title */}
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700">
            Title <span className="text-gray-400 font-normal">[3–80 chars]</span>
          </label>
          <input
            id="title"
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className={`mt-1 w-full rounded-xl border ${
              validTitle ? "border-gray-200" : "border-amber-300"
            } bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5`}
            placeholder="Main Campaign Title"
          />
          {!validTitle && (
            <p className="mt-1 text-xs text-amber-600">Must be 3–80 characters.</p>
          )}
        </div>

        {/* Short title */}
        <div>
          <label htmlFor="shorttitle" className="block text-sm font-medium text-gray-700">
            Short Title <span className="text-gray-400 font-normal">[3–60 chars]</span>
          </label>
          <input
            id="shorttitle"
            type="text"
            value={shortTitle}
            onChange={(e) => setShortTitle(e.target.value)}
            className={`mt-1 w-full rounded-xl border ${
              validShort ? "border-gray-200" : "border-amber-300"
            } bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5`}
            placeholder="Short name"
          />
          {!validShort && (
            <p className="mt-1 text-xs text-amber-600">Must be 3–60 characters.</p>
          )}
        </div>

        {/* Description */}
        <div className="md:col-span-2">
          <label htmlFor="description" className="block text-sm font-medium text-gray-700">
            Description
          </label>
          <textarea
            id="description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className={`mt-1 w-full rounded-xl border ${
              validDesc ? "border-gray-200" : "border-amber-300"
            } bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-black/5`}
            rows={3}
            placeholder="Campaign description..."
          />
          {!validDesc && (
            <p className="mt-1 text-xs text-amber-600">Description is required.</p>
          )}
        </div>

        {/* Dates */}
        <div>
          <label htmlFor="startdate" className="block text-sm font-medium text-gray-700">
            Start Date
          </label>
          <input
            id="startDate"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-black/5"
          />
        </div>

        <div>
          <label htmlFor="enddate" className="block text-sm font-medium text-gray-700">
            End Date
          </label>
          <input
            id="endDate"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-black/5"
          />
          {!validDates && (startDate || endDate) && (
            <p className="mt-1 text-xs text-amber-600">
              End date must be on/after start date.
            </p>
          )}
        </div>

        {/* Status */}
        <div>
          <label htmlFor="status" className="block text-sm font-medium text-gray-700">
            Status
          </label>
          <select
            id="status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="mt-1 w-full rounded-xl border border-gray-200 px-3 py-2 text-sm text-gray-900 focus:outline-none focus:ring-2 focus:ring-black/5"
          >
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </select>
        </div>
      </div>

      {/* Actions */}
      <div className="flex justify-end gap-2">
        <button
          type="submit"
          onClick={onSaveCampaignClicked}
          disabled={!canSave}
          className={`rounded-xl px-4 py-2 text-sm text-white ${
            canSave ? "bg-gray-900 hover:bg-black" : "bg-gray-400 cursor-not-allowed"
          }`}
        >
          Save
        </button>
      </div>
    </form>
  );
}
